import 'regenerator-runtime/runtime';
import 'quill/dist/quill.snow.css';
import { withSentry } from '@sentry/remix';
import { useRef } from 'react';
import {
  data,
  type LoaderFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from 'react-router';
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react';

import { useToast } from '~/components/ui/use-toast';

import { ClientHintCheck, getHints } from './components/client-hints.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { href as iconsHref } from './components/ui/icon.tsx';
import { Toaster } from './components/ui/toaster.tsx';
import fontStyleSheetUrl from './styles/font.css?url';
import quillOverridesStyleSheetUrl from './styles/quill-overrides.css?url';
import tailwindStyleSheetUrl from './styles/tailwind.css?url';
import { csrf } from './utils/csrf.server.ts';
import { getClientEnv } from './utils/env.server.ts';
import { combineHeaders, getDomainUrl } from './utils/misc.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings /*time*/ } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';

export const links: LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    // Preload CSS as a resource to avoid render blocking
    { rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
    { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
    {
      rel: 'alternate icon',
      type: 'image/png',
      href: '/favicons/favicon_32x32.png',
    },

    { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },

    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy

    { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon_32x32.svg' },
    { rel: 'stylesheet', href: quillOverridesStyleSheetUrl },
    { rel: 'stylesheet', href: fontStyleSheetUrl },
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: MetaFunction = () => {
  return [
    { title: 'Fighters Passport' },
    { name: 'description', content: 'Find BJJ Gyms & Classes' },
  ];
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  };
  return headers;
};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader');
  // const session = await time(() => requireSession(args), {
  //   timings,
  //   type: 'requireSession',
  //   desc: 'requireSession in root',
  // });

  const { toast, headers: toastHeaders } = await getToast(request);
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken(request.headers);

  return data(
    {
      // session,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        session: {},
      },
      ENV: getClientEnv(),
      toast,
      csrfToken,
    },
    {
      headers: combineHeaders(
        new Headers({ 'Server-Timing': timings.toString() }),
        toastHeaders,
        csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null,
      ),
    },
  );
}

function Document({
  children,
  nonce,
  env,
}: {
  children: React.ReactNode;
  nonce: string;
  env?: Record<string, string | undefined>;
}) {
  return (
    <html lang="en" className="size-full">
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        {/* https://stackoverflow.com/questions/77812591/when-opening-the-keyboard-on-mobile-how-can-i-force-my-content-to-shrink-to-fit */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, interactive-widget=resizes-content"
        />
        <Links nonce={nonce} />
      </head>
      <body className="font-base size-full bg-white font-sans font-normal leading-relaxed text-gray-800">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  const nonce = useNonce();
  const { toast } = useToast();
  const data = useLoaderData<typeof loader>();
  const lastToastIdRef = useRef<string | null>(null);

  if (data.toast && data.toast.id !== lastToastIdRef.current) {
    lastToastIdRef.current = data.toast.id;

    toast({
      title: data.toast.title,
      description: data.toast.description,
      type: data.toast.type ?? 'foreground',
      variant: data.toast.variant,
      // action: data.toast.action ? (
      //   <ToastAction altText={data.toast.action.text} onClick={() => console.log(data.toast.action.text)} >{data.toast.action.text}</ToastAction>
      //   ) : undefined,
    });
  }

  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
      <Document nonce={nonce} env={data.ENV}>
        <Outlet />
        <Toaster />
      </Document>
    </AuthenticityTokenProvider>
  );
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
}

export default withSentry(App);
